/**
 * Design Tokens - Single Source of Truth
 *
 * This file defines all design constants used throughout HookHouse Pro.
 * Import these tokens instead of hardcoding values to ensure consistency.
 */

// =============================================================================
// COLOR PALETTE
// =============================================================================

/**
 * Background colors - Use these for layered surfaces
 * Lower numbers = deeper/darker layers
 */
export const colors = {
  // Backgrounds (layered from deepest to surface)
  bg: {
    base: 'bg-slate-950',        // App background, deepest layer
    raised: 'bg-slate-900',      // Cards, panels, modals
    surface: 'bg-slate-800',     // Interactive surfaces, inputs
    hover: 'bg-slate-700',       // Hover states
    active: 'bg-slate-600',      // Active/pressed states
  },

  // Text colors (by importance)
  text: {
    primary: 'text-white',           // Headings, important text
    secondary: 'text-slate-300',     // Body text, descriptions
    muted: 'text-slate-400',         // Labels, hints
    subtle: 'text-slate-500',        // Disabled, placeholders
    disabled: 'text-slate-600',      // Disabled text
  },

  // Border colors
  border: {
    default: 'border-slate-800',     // Default borders
    subtle: 'border-slate-700',      // Subtle dividers
    strong: 'border-slate-600',      // Emphasized borders
    focus: 'border-accent',          // Focus rings
  },

  // Accent color (amber/gold brand color)
  accent: {
    solid: 'bg-accent',              // Solid accent background
    hover: 'bg-accent/90',           // Hover state
    muted: 'bg-accent/10',           // Muted background
    subtle: 'bg-accent/5',           // Very subtle background
    text: 'text-accent',             // Accent text
    border: 'border-accent',         // Accent border
  },

  // Semantic colors
  success: {
    solid: 'bg-green-600',
    hover: 'bg-green-500',
    muted: 'bg-green-500/10',
    text: 'text-green-400',
    border: 'border-green-500',
  },
  warning: {
    solid: 'bg-amber-600',
    hover: 'bg-amber-500',
    muted: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500',
  },
  danger: {
    solid: 'bg-red-600',
    hover: 'bg-red-500',
    muted: 'bg-red-500/10',
    text: 'text-red-400',
    border: 'border-red-500',
  },
  info: {
    solid: 'bg-blue-600',
    hover: 'bg-blue-500',
    muted: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500',
  },
} as const;

// =============================================================================
// SPACING
// =============================================================================

export const spacing = {
  xs: 'p-1',      // 4px
  sm: 'p-2',      // 8px
  md: 'p-4',      // 16px
  lg: 'p-6',      // 24px
  xl: 'p-8',      // 32px
} as const;

export const gap = {
  xs: 'gap-1',    // 4px
  sm: 'gap-2',    // 8px
  md: 'gap-4',    // 16px
  lg: 'gap-6',    // 24px
  xl: 'gap-8',    // 32px
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================

/**
 * Standard border radius values
 * Use consistently: sm for small elements, lg for cards, xl for modals
 */
export const radius = {
  none: 'rounded-none',
  sm: 'rounded-md',        // Small elements: badges, tags
  md: 'rounded-lg',        // Medium elements: buttons, inputs
  lg: 'rounded-xl',        // Large elements: cards
  xl: 'rounded-2xl',       // Extra large: modals, panels
  full: 'rounded-full',    // Circular: avatars, pills
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const shadows = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  glow: 'shadow-lg shadow-accent/20',  // Accent glow effect
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  // Font sizes
  size: {
    xs: 'text-xs',         // 12px
    sm: 'text-sm',         // 14px
    base: 'text-base',     // 16px
    lg: 'text-lg',         // 18px
    xl: 'text-xl',         // 20px
    '2xl': 'text-2xl',     // 24px
    '3xl': 'text-3xl',     // 30px
  },

  // Font weights
  weight: {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    black: 'font-black',
  },

  // Line heights
  leading: {
    tight: 'leading-tight',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
  },

  // Tracking (letter spacing)
  tracking: {
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    widest: 'tracking-widest',
  },
} as const;

// =============================================================================
// TRANSITIONS
// =============================================================================

export const transitions = {
  fast: 'transition-all duration-150',
  normal: 'transition-all duration-200',
  slow: 'transition-all duration-300',
  colors: 'transition-colors duration-200',
} as const;

// =============================================================================
// Z-INDEX LAYERS
// =============================================================================

export const zIndex = {
  base: 'z-0',
  dropdown: 'z-10',
  sticky: 'z-20',
  overlay: 'z-30',
  modal: 'z-40',
  popover: 'z-50',
  tooltip: 'z-[60]',
  toast: 'z-[70]',
  max: 'z-[100]',
} as const;

// =============================================================================
// COMPONENT PRESETS
// =============================================================================

/**
 * Pre-composed class combinations for common patterns
 */
export const presets = {
  // Card styles
  card: {
    base: `${colors.bg.raised} ${colors.border.default} border ${radius.lg}`,
    interactive: `${colors.bg.raised} ${colors.border.default} border ${radius.lg} hover:${colors.border.subtle} ${transitions.normal} cursor-pointer`,
    selected: `${colors.bg.raised} ${colors.accent.border} border ${radius.lg}`,
  },

  // Input styles
  input: {
    base: `${colors.bg.surface} ${colors.border.default} border ${radius.md} ${colors.text.primary} placeholder:${colors.text.subtle} focus:${colors.border.focus} focus:outline-none ${transitions.colors}`,
  },

  // Focus ring
  focusRing: 'focus:ring-2 focus:ring-accent/50 focus:ring-offset-0',

  // Scrollbar styling
  scrollbar: 'scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent',
} as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Combine multiple class strings, filtering out falsy values
 */
export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Create a variant map for component styling
 */
export function createVariants<T extends Record<string, string>>(variants: T): T {
  return variants;
}
